import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminClassesView.css';
import './SuperAdminDetailPages.css';
import { scopeForAdmin } from '../utils/schoolScope';

function SuperAdminClassesView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || adminData?.schoolId || null }), [adminData, saSchoolId]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editClass, setEditClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubjectTeacherModal, setShowSubjectTeacherModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [subjectTeacherAssignments, setSubjectTeacherAssignments] = useState({});
  const [detailClass, setDetailClass] = useState(null);
  const [detailStudents, setDetailStudents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [newClass, setNewClass] = useState({
    name: '',
    grade: '',
    teacherId: '',
    requiredSubjects: []
  });

  const availableSubjects = ["Mathematics", "English", "Science", "Biology", "Chemistry", "Social Studies", "History", "Spanish", "French", "Information Technology", "Physical Education", "Arts", "Agriculture", "Principles of Accounts (POA)", "Principles of Business (POB)", "Building Technology", "Home Economics", "Woodwork", "Reading Comprehension", "Penmanship"];

  const gradeLevels = [
    "Kindergarten", "Kind B",
    "Grade 1", "Grade 1 B",
    "Grade 2", "Grade 2 B",
    "Grade 3", "Grade 3 B",
    "Grade 4", "Grade 4 B",
    "Grade 5", "Grade 5 B",
    "Grade 6", "Grade 6 B",
    "Form 1", "Form 2", "Form 3", "Form 4", "Form 5", "Form 6"
  ];

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminClasses: Loading classes, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'classes'));
      let classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      classesData = scopeForAdmin(classesData, adminData);
      classesData.sort(schoolClassSortOrder);
      console.log('🔥 AdminClasses: Classes loaded', classesData.length, classesData);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminClasses: Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  const loadTeachers = useCallback(async () => {
    console.log('🔥 AdminClasses: Loading teachers, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const usersData = scopeForAdmin(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), adminData);
      // Show ALL teachers, not just regular type
      const teachersData = usersData.filter(user => user.role === 'Teacher');
      console.log('🔥 AdminClasses: Teachers loaded', teachersData.length, teachersData);
      setTeachers(teachersData);
    } catch (error) {
      console.error('🔥 AdminClasses: Error loading teachers:', error);
    }
  }, [adminData?.schoolId]);

  const loadSubjects = useCallback(async () => {
    console.log('🔥 AdminClasses: Loading subjects, schoolId:', adminData?.schoolId);
    try {
      const schoolId = adminData?.schoolId;
      const allSubjects = [];
      const seenIds = new Set();

      // 1. Load from top-level subjects collection (school-wide / global subjects)
      let globalQuery;
      if (schoolId) {
        globalQuery = query(collection(db, 'subjects'), where('schoolId', '==', schoolId));
      } else {
        globalQuery = query(collection(db, 'subjects'));
      }
      const globalSnapshot = await getDocs(globalQuery);
      globalSnapshot.docs.forEach(d => {
        const data = { id: d.id, ...d.data() };
        if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
      });

      // 2. Load from classes/{classId}/subjects subcollections
      let classQuery;
      if (schoolId) {
        classQuery = query(collection(db, 'classes'), where('schoolId', '==', schoolId));
      } else {
        classQuery = query(collection(db, 'classes'));
      }
      const classSnapshot = await getDocs(classQuery);
      for (const classDoc of classSnapshot.docs) {
        const subSnapshot = await getDocs(collection(db, 'classes', classDoc.id, 'subjects'));
        subSnapshot.docs.forEach(d => {
          const data = { id: d.id, ...d.data() };
          if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
        });
      }

      // 3. Load from gradeLevels/{gradeId}/subjects subcollections
      let gradeQuery;
      if (schoolId) {
        gradeQuery = query(collection(db, 'gradeLevels'), where('schoolId', '==', schoolId));
      } else {
        gradeQuery = query(collection(db, 'gradeLevels'));
      }
      const gradeSnapshot = await getDocs(gradeQuery);
      for (const gradeDoc of gradeSnapshot.docs) {
        const subSnapshot = await getDocs(collection(db, 'gradeLevels', gradeDoc.id, 'subjects'));
        subSnapshot.docs.forEach(d => {
          const data = { id: d.id, ...d.data() };
          if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
        });
      }

      console.log('🔥 AdminClasses: Subjects loaded', allSubjects.length, allSubjects);
      setSubjects(allSubjects);
    } catch (error) {
      console.error('🔥 AdminClasses: Error loading subjects:', error);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadClasses();
    loadTeachers();
    loadSubjects();
  }, [loadClasses, loadTeachers, loadSubjects]);

  const handleAddClass = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'classes'), {
        ...newClass,
        isActive: true,
        createdAt: new Date().toISOString(),
        requiredSubjects: newClass.requiredSubjects || [],
        subjectTeacherAssignments: {},
        schoolId: adminData?.schoolId || null
      });
      setShowAddForm(false);
      setNewClass({ name: '', grade: '', teacherId: '', requiredSubjects: [] });
      loadClasses();
    } catch (error) {
      console.error('Error adding class:', error);
      alert('Failed to add class');
    }
  };

  const handleDeleteClass = async (classId) => {
    const confirmed = window.confirm(
      'Are you sure you want to PERMANENTLY DELETE this class?\n\nAll students, subjects, assignments, and grades within this class will also be deleted. This action CANNOT be undone.'
    );
    if (!confirmed) return false;
    try {
      // Delete all students in the class subcollection first
      const studentsSnapshot = await getDocs(collection(db, 'classes', classId, 'students'));
      for (const studentDoc of studentsSnapshot.docs) {
        await deleteDoc(doc(db, 'classes', classId, 'students', studentDoc.id));
      }
      // Delete all subjects
      const subjectsSnapshot = await getDocs(collection(db, 'classes', classId, 'subjects'));
      for (const subjectDoc of subjectsSnapshot.docs) {
        await deleteDoc(doc(db, 'classes', classId, 'subjects', subjectDoc.id));
      }
      // Delete the class document itself
      await deleteDoc(doc(db, 'classes', classId));
      loadClasses();
      return true;
    } catch (error) {
      console.error('Error deleting class:', error);
      alert('Failed to delete class: ' + error.message);
      return false;
    }
  };

  const handleEditClick = (cls) => {
    setEditClass(cls);
    setIsEditing(true);
  };

  // Resolve schoolId -> school name (match doc id, uuid field, or name; case-insensitive)
  const resolveSchoolName = (schoolId) => {
    if (!schoolId) return 'N/A';
    const sid = String(schoolId).toLowerCase();
    const match = saSchools.find(s =>
      String(s.id).toLowerCase() === sid ||
      String(s.uuid || '').toLowerCase() === sid ||
      String(s.name || '').toLowerCase() === sid
    );
    return match ? (match.name || match.id) : String(schoolId);
  };

  const handleCloseModal = () => {
    setIsEditing(false);
    setEditClass(null);
  };

  const handleUpdateClass = async (e) => {
    e.preventDefault();
    if (!editClass) return;
    try {
      await updateDoc(doc(db, 'classes', editClass.id), {
        name: editClass.name,
        grade: editClass.grade,
        teacherId: editClass.teacherId || null,
        updatedAt: new Date().toISOString()
      });
      handleCloseModal();
      loadClasses();
    } catch (error) {
      console.error('Error updating class:', error);
      alert('Failed to update class');
    }
  };

  const handleAssignSubject = async (subjectId) => {
    if (!selectedClass) return;
    try {
      await updateDoc(doc(db, 'subjects', subjectId), {
        classId: selectedClass.id
      });
      loadSubjects();
      alert('Subject assigned to class');
    } catch (error) {
      console.error('Error assigning subject:', error);
      alert('Failed to assign subject');
    }
  };

  const handleRemoveSubject = async (subjectId) => {
    if (!selectedClass) return;
    try {
      await updateDoc(doc(db, 'subjects', subjectId), {
        classId: null
      });
      loadSubjects();
      alert('Subject removed from class');
    } catch (error) {
      console.error('Error removing subject:', error);
      alert('Failed to remove subject');
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? teacher.username : 'Unassigned';
  };

  const openClassDetail = async (cls) => {
    setDetailClass(cls);
    setDetailLoading(true);
    setDetailStudents([]);
    try {
      const snap = await getDocs(collection(db, 'classes', cls.id, 'students'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isActive !== false);
      list.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
      setDetailStudents(list);
    } catch (e) {
      console.error('Error loading class students:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const getClassSubjects = () => {
    if (!selectedClass) return [];
    return subjects.filter(s => s.classId === selectedClass.id);
  };

  const getAvailableSubjects = () => {
    if (!selectedClass) return [];
    return subjects.filter(s => s.classId !== selectedClass.id);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Full-screen class detail page — replaces the list (not an overlay)
  if (detailClass) {
    const cls = detailClass;
    const assignments = cls.subjectTeacherAssignments || {};
    return (
      <div className="detail-page">
        <button className="dp-back" onClick={() => setDetailClass(null)}>
          ◄ Back to Classes
        </button>

        <div className="dp-hero">
          <div className="dp-avatar">🏫</div>
          <div className="dp-hero-text">
            <h2>{cls.name}</h2>
            <p>{cls.grade || 'No grade'} • {resolveSchoolName(cls.schoolId)}</p>
            <span className={`dp-badge ${cls.isActive === false ? 'red' : 'green'}`}>
              {cls.isActive === false ? 'Inactive' : 'Active'}
            </span>
          </div>
        </div>

        <div className="dp-card">
          <h3>Class Information</h3>
          <div className="dp-grid">
            <div className="dp-field">
              <div className="dp-label">Class Name</div>
              <div className="dp-value">{cls.name || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Grade Level</div>
              <div className="dp-value">{cls.grade || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">School</div>
              <div className="dp-value">{resolveSchoolName(cls.schoolId)}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Home Teacher</div>
              <div className="dp-value">{getTeacherName(cls.teacherId)}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Capacity</div>
              <div className="dp-value">{cls.capacity != null ? cls.capacity : 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Students Enrolled</div>
              <div className="dp-value">{detailLoading ? '…' : detailStudents.length}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Class ID</div>
              <div className="dp-value">{cls.id}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Created</div>
              <div className="dp-value">{cls.createdAt ? new Date(cls.createdAt).toLocaleDateString() : 'N/A'}</div>
            </div>
          </div>
        </div>

        <div className="dp-card">
          <h3>Subjects &amp; Teachers ({(cls.requiredSubjects || []).length})</h3>
          {(cls.requiredSubjects || []).length === 0 ? (
            <p className="dp-empty">No subjects assigned</p>
          ) : (
            cls.requiredSubjects.map(subject => {
              const teacherId = assignments[subject];
              return (
                <div key={subject} className="dp-row">
                  <span className="dp-row-main">{subject}</span>
                  <span className="dp-row-sub">{teacherId ? getTeacherName(teacherId) : 'No teacher assigned'}</span>
                </div>
              );
            })
          )}
        </div>

        <div className="dp-card">
          <h3>Students ({detailLoading ? '…' : detailStudents.length})</h3>
          {detailLoading ? (
            <p className="dp-empty">Loading students...</p>
          ) : detailStudents.length === 0 ? (
            <p className="dp-empty">No students enrolled</p>
          ) : (
            detailStudents.map(s => (
              <div key={s.id} className="dp-row">
                <span className="dp-row-main">{s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unknown'}</span>
                <span className="dp-row-sub">{s.studentId || ''}</span>
              </div>
            ))
          )}
        </div>

        <div className="dp-actions">
          <button
            type="button"
            className="dp-btn-info"
            onClick={() => { const c = cls; setDetailClass(null); setSelectedClass(c); setShowSubjectModal(true); }}
          >
            📚 Manage Subjects
          </button>
          <button
            type="button"
            className="dp-btn-info"
            onClick={() => { const c = cls; setDetailClass(null); setSelectedClass(c); setSubjectTeacherAssignments(c.subjectTeacherAssignments || {}); setShowSubjectTeacherModal(true); }}
          >
            👨‍🏫 Subject Teachers
          </button>
          <button
            type="button"
            className="dp-btn-primary"
            onClick={() => { const c = cls; setDetailClass(null); handleEditClick(c); }}
          >
            Edit
          </button>
          <button
            type="button"
            className="dp-btn-danger"
            onClick={async () => { if (await handleDeleteClass(cls.id)) setDetailClass(null); }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-classes-view">
      <div className="view-header">
        <h2>Classes</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Class
        </button>
      </div>

      <p className="count-text">Classes: {classes.length}</p>

      {showAddForm && (
        <div className="add-class-form">
          <h3>Create New Class</h3>
          <form onSubmit={handleAddClass}>
            <div className="form-group">
              <label>Class Name *</label>
              <input
                type="text"
                value={newClass.name}
                onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Grade *</label>
              <select
                value={newClass.grade}
                onChange={(e) => setNewClass({ ...newClass, grade: e.target.value })}
                required
              >
                <option value="">Select Grade</option>
                {gradeLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Home Teacher</label>
              <select
                value={newClass.teacherId}
                onChange={(e) => setNewClass({ ...newClass, teacherId: e.target.value })}
              >
                <option value="">Unassigned</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.username}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Required Subjects</label>
              <div className="subjects-grid">
                {availableSubjects.map(subject => (
                  <label key={subject} className="subject-checkbox">
                    <input
                      type="checkbox"
                      checked={newClass.requiredSubjects?.includes(subject) || false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewClass({
                            ...newClass,
                            requiredSubjects: [...(newClass.requiredSubjects || []), subject]
                          });
                        } else {
                          setNewClass({
                            ...newClass,
                            requiredSubjects: (newClass.requiredSubjects || []).filter(s => s !== subject)
                          });
                        }
                      }}
                    />
                    <span>{subject}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Class</button>
            </div>
          </form>
        </div>
      )}

      {classes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏫</div>
          <h3>No Classes</h3>
          <p>Create your first class to get started</p>
        </div>
      ) : (
        <div className="classes-list">
          {classes.map(cls => (
            <div key={cls.id} className="class-card" onClick={() => openClassDetail(cls)} style={{ cursor: 'pointer' }}>
              <div className="class-icon">🏫</div>
              <div className="class-info">
                <h4>{cls.name}</h4>
                <p>{cls.grade}</p>
                <p>Teacher: {getTeacherName(cls.teacherId)}</p>
              </div>
              <div className="class-status">
                <span className="status-dot active"></span>
              </div>
              <div className="class-actions">
                <button
                  className="manage-subjects-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedClass(cls);
                    setShowSubjectModal(true);
                  }}
                >
                  📚 Subjects
                </button>
                <button
                  className="manage-subject-teachers-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedClass(cls);
                    setSubjectTeacherAssignments(cls.subjectTeacherAssignments || {});
                    setShowSubjectTeacherModal(true);
                  }}
                >
                  👨‍🏫 Subject Teachers
                </button>
                <button
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClass(cls.id);
                  }}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isEditing && editClass && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Class</h3>
            <form onSubmit={handleUpdateClass}>
              <div className="form-group">
                <label>Class Name *</label>
                <input
                  type="text"
                  value={editClass.name}
                  onChange={(e) => setEditClass({ ...editClass, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Grade *</label>
                <select
                  value={editClass.grade}
                  onChange={(e) => setEditClass({ ...editClass, grade: e.target.value })}
                  required
                >
                  <option value="">Select Grade</option>
                  {gradeLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Home Teacher</label>
                <select
                  value={editClass.teacherId || ''}
                  onChange={(e) => setEditClass({ ...editClass, teacherId: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {teachers.map(teacher => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.username}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSubjectModal && selectedClass && (
        <div className="modal-overlay" onClick={() => setShowSubjectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Manage Subjects - {selectedClass.name}</h3>
            <div className="subject-management">
              <h4>Available Subjects</h4>
              {getAvailableSubjects().length === 0 ? (
                <p className="no-data">No available subjects to assign</p>
              ) : (
                <div className="subjects-list">
                  {getAvailableSubjects().map(subject => (
                    <div key={subject.id} className="subject-item">
                      <span>{subject.name} ({subject.category})</span>
                      <button onClick={() => handleAssignSubject(subject.id)}>
                        Assign
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <h4>Assigned Subjects</h4>
              {getClassSubjects().length === 0 ? (
                <p className="no-data">No subjects assigned to this class</p>
              ) : (
                <div className="subjects-list">
                  {getClassSubjects().map(subject => (
                    <div key={subject.id} className="subject-item">
                      <span>{subject.name} ({subject.category})</span>
                      <button className="remove-button" onClick={() => handleRemoveSubject(subject.id)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowSubjectModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubjectTeacherModal && selectedClass && (
        <div className="modal-overlay" onClick={() => setShowSubjectTeacherModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Subject Teachers - {selectedClass.name}</h3>
            <div className="subject-teacher-management">
              <div className="form-group">
                <label>Home Teacher</label>
                <select
                  value={selectedClass.teacherId || ''}
                  onChange={async (e) => {
                    await updateDoc(doc(db, 'classes', selectedClass.id), {
                      teacherId: e.target.value || null
                    });
                    loadClasses();
                  }}
                >
                  <option value="">Unassigned</option>
                  {teachers.map(teacher => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.username}
                    </option>
                  ))}
                </select>
              </div>

              <h4>Subject Teacher Assignments</h4>
              {availableSubjects.map(subject => (
                <div key={subject} className="subject-teacher-row">
                  <span>{subject}</span>
                  <select
                    value={subjectTeacherAssignments[subject] || ''}
                    onChange={async (e) => {
                      const newAssignments = { ...subjectTeacherAssignments };
                      if (e.target.value) {
                        newAssignments[subject] = e.target.value;
                      } else {
                        delete newAssignments[subject];
                      }
                      setSubjectTeacherAssignments(newAssignments);
                      await updateDoc(doc(db, 'classes', selectedClass.id), {
                        subjectTeacherAssignments: newAssignments
                      });
                    }}
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.username}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowSubjectTeacherModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default SuperAdminClassesView;
